import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Share2, Copy, MessageSquare, Settings, ExternalLink } from 'lucide-react';
import { usePublicEventPages } from '@/hooks/usePublicEventPages';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { generatePublicEventUrl } from '@/lib/urlUtils';

interface ShareableEventButtonProps {
  eventId: string;
}

export const ShareableEventButton: React.FC<ShareableEventButtonProps> = ({ eventId }) => {
  const [publicPageUrl, setPublicPageUrl] = useState<string | null>(null);
  const [loadingLocal, setLoadingLocal] = useState(false);
  const { createPublicPage, getPublicPageForEvent, loading } = usePublicEventPages();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const cached = localStorage.getItem(`public_event_url_${eventId}`);
    if (cached) setPublicPageUrl(cached);
  }, [eventId]);

  const ensurePageAndGetUrl = async (): Promise<string | null> => {
    setLoadingLocal(true);
    try {
      const existingPage = await getPublicPageForEvent(eventId);
      if (existingPage) {
        return generatePublicEventUrl(existingPage.slug);
      }
      const page = await createPublicPage(eventId);
      if (page) {
        return generatePublicEventUrl(page.slug);
      }
      return null;
    } finally {
      setLoadingLocal(false);
    }
  };

  const handleCreateOrShow = async () => {
    const url = await ensurePageAndGetUrl();
    if (url) {
      setPublicPageUrl(url);
      localStorage.setItem(`public_event_url_${eventId}`, url);
      toast({ title: 'Page prête', description: 'Lien disponible.' });
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Copié !' });
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const shareOnWhatsApp = (url: string) => {
    const message = encodeURIComponent(`Vous êtes invité(e) à cet événement ! Confirmez votre présence : ${url}`);
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const getSlugFromUrl = (url: string) => {
    const match = url.match(/\/public-event\/([^\/]+)/);
    return match ? match[1] : '';
  };

  return (
    <div className="space-y-3">
      {publicPageUrl ? (
        <div className="space-y-2">
          <Button
            onClick={() => navigate(`/public-event-admin/${getSlugFromUrl(publicPageUrl)}`)}
            className="w-full mb-2 bg-accent text-accent-foreground hover:bg-accent/90"
          >
            <Settings className="h-4 w-4 mr-2" />
            Modifier la page partagée
          </Button>
          
          <div className="flex space-x-2">
            <Input value={publicPageUrl} readOnly className="flex-1" />
            <Button variant="outline" size="icon" onClick={() => copyToClipboard(publicPageUrl)}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" className="flex-1" onClick={() => window.open(publicPageUrl, '_blank')}>
              <ExternalLink className="h-4 w-4 mr-2" /> Voir la page
            </Button>
            <Button variant="outline" className="flex-1" onClick={() => shareOnWhatsApp(publicPageUrl)}>
              <MessageSquare className="h-4 w-4 mr-2" /> WhatsApp
            </Button>
          </div>
        </div>
      ) : (
        <Button 
          variant="outline" 
          className="w-full" 
          onClick={handleCreateOrShow}
          disabled={loading || loadingLocal}
        >
          <Share2 className="h-4 w-4 mr-2" />
          {loadingLocal ? 'Création...' : 'Créer une page partageable'}
        </Button>
      )}
    </div>
  );
};